import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestJson } from './client';

export interface Person {
  id: string;
  fullName: string;
  position: string | null;
  photoUrl: string | null;
}

export interface OrgDepartment {
  id: string;
  parentId: string | null;
  name: string;
  heads: { id: string; coHead: boolean }[];
  directCount: number;
  totalCount: number;
  childIds: string[];
}

export interface OrgStructure {
  rootIds: string[];
  employeeCount: number;
  departments: OrgDepartment[];
  people: Record<string, Person>;
}

export interface DepartmentMember extends Person {
  departmentsCount: number;
}

export interface SearchResult {
  employees: (Person & { city: string | null; departments: string[] })[];
  departments: { id: string; name: string }[];
}

export function useOrgStructure() {
  return useQuery({
    queryKey: ['org', 'structure'],
    queryFn: () => requestJson<OrgStructure>('/api/v1/org/structure'),
    staleTime: 5 * 60_000,
  });
}

export function useDepartmentMembers(departmentId: string | null) {
  return useQuery({
    queryKey: ['org', 'members', departmentId],
    queryFn: () =>
      requestJson<{ items: DepartmentMember[] }>(`/api/v1/org/departments/${departmentId}/members`),
    enabled: Boolean(departmentId),
    staleTime: 5 * 60_000,
  });
}

export function useOrgSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['org', 'search', q],
    queryFn: () => requestJson<SearchResult>(`/api/v1/org/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Индексы структуры: подразделение по id и путь от корня. */
export function indexStructure(structure: OrgStructure) {
  const byId = new Map(structure.departments.map((department) => [department.id, department]));
  const pathTo = (id: string): OrgDepartment[] => {
    const path: OrgDepartment[] = [];
    const seen = new Set<string>();
    let current = byId.get(id);
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      path.unshift(current);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return path;
  };
  return { byId, pathTo };
}
